import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'gradient' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Enhanced Button Component
 * 
 * Beautiful, animated button with multiple variants.
 * Includes loading states, icons, and smooth transitions.
 * 
 * Variants:
 * - primary: Solid violet button
 * - secondary: Outlined button
 * - ghost: Transparent with hover
 * - danger: Red for destructive actions
 * - gradient: Vibrant gradient background
 * - glass: Glassmorphism effect
 * 
 * @example
 * <Button variant="gradient" leftIcon={<Sparkles />} onClick={handleClick}>
 *   Generate Dashboard
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center gap-2
      font-medium rounded-xl
      transition-all duration-200 ease-out
      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50
      disabled:opacity-50 disabled:cursor-not-allowed
      relative overflow-hidden
    `;

    const variants = {
      primary: `
        bg-primary-500 text-white
        hover:bg-primary-600 hover:shadow-lifted hover:shadow-primary-500/30
      `,
      secondary: `
        bg-transparent border border-khiimori-border text-khiimori-text-primary
        hover:bg-khiimori-bg-tertiary hover:border-khiimori-border
      `,
      ghost: `
        bg-transparent text-khiimori-text-secondary
        hover:bg-khiimori-bg-tertiary hover:text-khiimori-text-primary
      `,
      danger: `
        bg-error-500 text-khiimori-text-primary
        hover:bg-error-600 hover:shadow-lifted hover:shadow-error-500/30
      `,
      gradient: `
        text-khiimori-text-primary bg-gradient-primary
        hover:shadow-lifted hover:shadow-primary-500/40
      `,
      glass: `
        text-khiimori-text-primary border border-khiimori-border glass
        hover:border-khiimori-border hover:bg-khiimori-bg-tertiary
      `,
    };

    const sizes = {
      sm: 'h-9 px-4 text-sm',
      md: 'h-11 px-5 text-sm',
      lg: 'h-13 px-7 text-base',
    };

    const MotionButton = motion.button;

    return (
      <MotionButton
        ref={ref}
        whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
        whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {/* Hover shine effect for gradient buttons */}
        {variant === 'gradient' && !disabled && !isLoading && (
          <motion.div
            className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity"
            style={{
              background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
            }}
          />
        )}
        
        {isLoading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        <span className="relative z-10">{children}</span>
        {!isLoading && rightIcon}
      </MotionButton>
    );
  }
);

Button.displayName = 'Button';